/**
 * useShaderScripts — manages the lifecycle of JS-driven GLSL shader materials.
 *
 * For each ColoredGeometry that has a shaderScript entry, this hook:
 *  1. Looks up the .js source from the provided project files.
 *  2. Spawns a sandboxed ShaderWorker for each unique filename.
 *  3. On READY: creates a THREE.ShaderMaterial on the main thread.
 *  4. Registers the material via onShaderMaterialReady(geometryUuid, material).
 *  5. When tick() is present, drives a rAF loop that sends TICK messages to the
 *     worker each frame and applies the resulting UNIFORMS updates directly to
 *     the material (no React re-render).
 *  6. On params change: forwards PARAMS to all running workers.
 *  7. On unmount / recompile: sends STOP and terminates workers.
 *
 * Inter-script communication:
 *   The same __publish__ / __subscribe__ pub/sub system used by texture scripts.
 *   The sharedStateRef and fan-out relay are shared with useTextureScripts via
 *   the externalSharedStateRef and externalPublishToAll parameters so that
 *   shader and texture workers can communicate with each other.
 *
 * Must be called inside the R3F <Canvas> so useThree() is available.
 *
 * Design notes:
 *   - THREE.ShaderMaterial is created on the main thread (never in the worker).
 *   - Uniform values are plain numbers/arrays over postMessage — no GPU objects
 *     ever cross the worker boundary.
 *   - The rAF loop only runs when at least one worker has hasTick=true.
 */

import { useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import type { ColoredGeometry } from '@/components/viewer/types';
import type { ProjectFile } from '@/components/files/FileManager';
import type { Parameter } from '@shared/types';
import { createShaderMaterial } from './materialUtils';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parametersToRecord(parameters: Parameter[]): Record<string, unknown> {
  const record: Record<string, unknown> = {};
  for (const p of parameters) {
    record[p.name] = p.value;
  }
  return record;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A live shader entry managed for a single filename */
interface ShaderEntry {
  worker: Worker;
  /** null until the READY message arrives */
  material: THREE.ShaderMaterial | null;
  /** true if the script exported a tick() function */
  hasTick: boolean;
  /** source code used to create this entry (for hot-reload detection) */
  scriptCode: string;
  /** geometry UUID that owns this entry */
  geometryUuid: string;
  /** color of the geometry (injected as uColor uniform) */
  color: THREE.Color;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param coloredGeometries   The full list of colored geometries from the compiler.
 * @param projectFiles        Project files list to look up shader .js sources.
 * @param onShaderMaterialReady  Called once per geometry when its ShaderMaterial is ready.
 *                               The material ref map is updated before this callback fires.
 * @param parameters          Current parameter values — forwarded to workers on change.
 * @param onHasTickingShaders Called whenever the set of tick-enabled shaders changes.
 * @param shaderMaterialMapRef  Outer map keyed by geometry UUID → ShaderMaterial.
 *                              Written by this hook; read by ModelRenderer.
 * @param externalSharedStateRef  Shared pub/sub state from useTextureScripts (shared channel).
 * @param externalGetAllWorkers   Returns all currently active workers (texture + shader)
 *                                so PUBLISH can fan out across both systems.
 */
export function useShaderScripts(
  coloredGeometries: ColoredGeometry[],
  projectFiles: ProjectFile[],
  onShaderMaterialReady: () => void,
  parameters: Parameter[],
  onHasTickingShaders: (hasTicking: boolean) => void,
  shaderMaterialMapRef: React.MutableRefObject<
    Map<string, THREE.ShaderMaterial>
  >,
  externalSharedStateRef?: React.MutableRefObject<Map<string, unknown>>,
  externalGetAllWorkers?: () => Worker[],
): { getWorkers: () => Worker[] } {
  const { invalidate } = useThree();
  const invalidateRef = useRef(invalidate);
  invalidateRef.current = invalidate;

  const onShaderMaterialReadyRef = useRef(onShaderMaterialReady);
  onShaderMaterialReadyRef.current = onShaderMaterialReady;

  const onHasTickingRef = useRef(onHasTickingShaders);
  onHasTickingRef.current = onHasTickingShaders;

  const parametersRef = useRef(parameters);
  parametersRef.current = parameters;

  /** filename → ShaderEntry */
  const entriesRef = useRef<Map<string, ShaderEntry>>(new Map());

  /**
   * Local shared-state cache for pub/sub.
   * When externalSharedStateRef is provided we use that instead so state is
   * shared across texture + shader workers. Otherwise we maintain our own.
   */
  const localSharedStateRef = useRef<Map<string, unknown>>(new Map());
  const sharedStateRef = externalSharedStateRef ?? localSharedStateRef;

  /** rAF ID for the tick loop */
  const rafIdRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(performance.now());

  // ---------------------------------------------------------------------------
  // Broadcast tick state
  // ---------------------------------------------------------------------------

  const broadcastTickState = useCallback(() => {
    const hasTicking = [...entriesRef.current.values()].some(
      (e) => e.hasTick && e.material !== null,
    );
    onHasTickingRef.current(hasTicking);
    return hasTicking;
  }, []);

  // ---------------------------------------------------------------------------
  // rAF tick loop
  // ---------------------------------------------------------------------------

  const stopTickLoop = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  const startTickLoop = useCallback(() => {
    if (rafIdRef.current !== null) return; // already running
    const loop = () => {
      const elapsedMs = performance.now() - startTimeRef.current;
      for (const entry of entriesRef.current.values()) {
        if (entry.hasTick && entry.worker && entry.material) {
          entry.worker.postMessage({ type: 'TICK', elapsedMs });
        }
      }
      rafIdRef.current = requestAnimationFrame(loop);
    };
    rafIdRef.current = requestAnimationFrame(loop);
  }, []);

  const syncTickLoop = useCallback(() => {
    const hasTicking = broadcastTickState();
    if (hasTicking) {
      startTickLoop();
    } else {
      stopTickLoop();
    }
  }, [broadcastTickState, startTickLoop, stopTickLoop]);

  // ---------------------------------------------------------------------------
  // Fan-out PUBLISH to all workers (texture + shader)
  // ---------------------------------------------------------------------------

  const fanOutPublish = useCallback(
    (sourceWorker: Worker, key: string, value: unknown) => {
      sharedStateRef.current.set(key, value);

      // Fan out to all other shader workers
      for (const entry of entriesRef.current.values()) {
        if (entry.worker !== sourceWorker) {
          entry.worker.postMessage({ type: 'DATA', key, value });
        }
      }

      // Fan out to texture workers (if relay is provided)
      if (externalGetAllWorkers) {
        for (const w of externalGetAllWorkers()) {
          if (w !== sourceWorker) {
            w.postMessage({ type: 'DATA', key, value });
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [externalGetAllWorkers],
  );

  // ---------------------------------------------------------------------------
  // Spawn a shader worker
  // ---------------------------------------------------------------------------

  const spawnWorker = useCallback(
    (
      entryKey: string,
      geometryUuid: string,
      scriptCode: string,
      color: THREE.Color,
    ) => {
      const worker = new Worker(
        new URL('../../../worker/shaderWorker.ts', import.meta.url),
        { type: 'module' },
      );

      const entry: ShaderEntry = {
        worker,
        material: null,
        hasTick: false,
        scriptCode,
        geometryUuid,
        color,
      };
      entriesRef.current.set(entryKey, entry);

      worker.onmessage = (event: MessageEvent) => {
        const msgType = (event.data as { type: string }).type;

        if (msgType === 'READY') {
          const {
            vertexShader,
            fragmentShader,
            initialUniforms,
            hasTick,
          } = event.data as {
            vertexShader: string;
            fragmentShader: string;
            initialUniforms: Record<string, unknown>;
            hasTick: boolean;
          };

          const currentEntry = entriesRef.current.get(entryKey);
          if (!currentEntry) return;

          const isTransparent = color.r < 1 || color.g < 1 || color.b < 1
            ? false  // color transparency is handled by the GLSL itself
            : false; // shader is fully in control of transparency

          const material = createShaderMaterial(
            vertexShader,
            fragmentShader,
            initialUniforms,
            currentEntry.color,
          );

          currentEntry.material = material;
          currentEntry.hasTick = hasTick;

          // Register in the material map so ModelRenderer can pick it up
          shaderMaterialMapRef.current.set(geometryUuid, material);

          console.log(
            `[ShaderScripts] Material ready for "${entryKey}" (hasTick=${hasTick})`,
          );

          // Notify the parent so it can re-render
          onShaderMaterialReadyRef.current();
          invalidateRef.current();
          syncTickLoop();
        }

        if (msgType === 'UNIFORMS') {
          const currentEntry = entriesRef.current.get(entryKey);
          if (!currentEntry?.material) return;

          const { values } = event.data as {
            values: Record<string, unknown>;
          };

          // Apply serialized uniform values to the live material
          const uniforms = currentEntry.material.uniforms;
          for (const [key, value] of Object.entries(values)) {
            if (key in uniforms) {
              uniforms[key].value = value;
            } else {
              uniforms[key] = { value };
            }
          }
          currentEntry.material.needsUpdate = false; // uniforms don't need needsUpdate

          // Wake the demand-render loop
          invalidateRef.current();
        }

        if (msgType === 'ERROR') {
          console.error(
            `[ShaderScripts] Error in shader script "${entryKey}":`,
            (event.data as { message: string }).message,
          );
        }

        if (msgType === 'PUBLISH') {
          const { key, value } = event.data as { key: string; value: unknown };
          fanOutPublish(worker, key, value);
        }
      };

      worker.onerror = (err) => {
        console.error(`[ShaderScripts] Worker onerror for "${entryKey}":`, err);
      };

      // Send the script to the worker
      const params = parametersToRecord(parametersRef.current);
      const sharedStateSnapshot = Object.fromEntries(sharedStateRef.current);

      worker.postMessage({
        type: 'START',
        scriptCode,
        params,
        ...(Object.keys(sharedStateSnapshot).length > 0
          ? { sharedState: sharedStateSnapshot }
          : {}),
      });
    },
    [fanOutPublish, syncTickLoop, shaderMaterialMapRef],
  );

  // ---------------------------------------------------------------------------
  // Main effect: diff geometries, spawn/stop workers as needed
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const entries = entriesRef.current;
    const shaderMap = shaderMaterialMapRef.current;

    // Build the set of needed entries from current geometries
    // entryKey → { geometryUuid, scriptCode, color }
    const needed = new Map<
      string,
      { geometryUuid: string; scriptCode: string; color: THREE.Color }
    >();

    for (const cg of coloredGeometries) {
      if (!cg.shaderScript) continue;
      const filename = cg.shaderScript;
      const file = projectFiles.find((f) => f.filename === filename);
      if (!file || typeof file.content !== 'string') {
        if (!file) {
          console.warn(
            `[ShaderScripts] File "${filename}" not found in projectFiles`,
            projectFiles.map((f) => f.filename),
          );
        }
        continue;
      }

      // Key: filename@geometryUuid — each geometry gets its own worker so
      // uColor can differ. (Multiple geometries with the same script file but
      // different colors each need their own ShaderMaterial.)
      const entryKey = `${filename}@${cg.geometry.uuid}`;
      const color = new THREE.Color(
        cg.color[0],
        cg.color[1],
        cg.color[2],
      );

      needed.set(entryKey, {
        geometryUuid: cg.geometry.uuid,
        scriptCode: file.content,
        color,
      });
    }

    // Stop and remove entries no longer needed
    for (const [key, entry] of entries) {
      if (!needed.has(key)) {
        console.log(`[ShaderScripts] Stopping worker for "${key}"`);
        entry.worker.postMessage({ type: 'STOP' });
        entry.worker.terminate();
        entry.material?.dispose();
        shaderMap.delete(entry.geometryUuid);
        entries.delete(key);
      }
    }

    // Sync tick loop after removals
    syncTickLoop();

    // Start or hot-reload entries for needed shaders
    for (const [key, { geometryUuid, scriptCode, color }] of needed) {
      const existing = entries.get(key);

      if (existing) {
        // Same script content — nothing to do (color changes don't reload;
        // color only updates when the worker respawns on recompile)
        if (existing.scriptCode === scriptCode) {
          continue;
        }

        // Content changed — hot reload
        console.log(`[ShaderScripts] Content changed for "${key}", recreating`);
        existing.worker.postMessage({ type: 'STOP' });
        existing.worker.terminate();
        existing.material?.dispose();
        shaderMap.delete(geometryUuid);
        entries.delete(key);
      }

      console.log(`[ShaderScripts] Spawning worker for "${key}"`);
      spawnWorker(key, geometryUuid, scriptCode, color);
    }
  }, [
    coloredGeometries,
    projectFiles,
    spawnWorker,
    syncTickLoop,
    shaderMaterialMapRef,
  ]);

  // ---------------------------------------------------------------------------
  // Forward params changes to running workers
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const entries = entriesRef.current;
    if (entries.size === 0) return;
    const params = parametersToRecord(parameters);
    for (const entry of entries.values()) {
      entry.worker.postMessage({ type: 'PARAMS', params });
    }
  }, [parameters]);

  // ---------------------------------------------------------------------------
  // Cleanup on unmount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    return () => {
      console.log('[ShaderScripts] Unmounting, stopping all workers');
      stopTickLoop();
      for (const entry of entriesRef.current.values()) {
        entry.worker.postMessage({ type: 'STOP' });
        entry.worker.terminate();
        entry.material?.dispose();
      }
      entriesRef.current.clear();
      shaderMaterialMapRef.current.clear();
      onHasTickingRef.current(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Worker getter — used by TextureScriptManager to fan out pub/sub
  // ---------------------------------------------------------------------------

  const getWorkers = useCallback((): Worker[] => {
    return [...entriesRef.current.values()].map((e) => e.worker);
  }, []);

  return { getWorkers };
}

/**
 * Returns a function that, when called, returns all currently active shader
 * worker instances. Used by useTextureScripts to fan out pub/sub to shader workers.
 */
export function useShaderWorkerGetter(
  entriesRef: React.MutableRefObject<Map<string, { worker: Worker }>>,
): () => Worker[] {
  return useCallback(() => {
    return [...entriesRef.current.values()].map((e) => e.worker);
  }, [entriesRef]);
}
